import {
    Component,
    HostListener,
    OnInit,
    inject,
    signal,
} from '@angular/core';
import { Location } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { LayoutService } from '@cvx/nextpage';
import { PagedResponse } from 'src/app/shared/models/paged-response.model';
import { LifeCyclePhase } from '../models/life-cycle-phase.enum';
import { ToolProductTeam } from '../models/tool-product-team.model';
import { ToolPreview } from '../models/tool-preview.model';
import { ToolService } from '../services/tool.service';
import { DialogHelper } from 'src/app/shared/utilities/helpers/dialog-helper/dialog-helper';
import { WindowHelper } from 'src/app/shared/utilities/helpers/window-helper/window-helper';
import { PersonaService } from 'src/app/shared/services/persona.service';
import { ToolsDataViewComponent } from '../components/tools-data-view/tools-data-view.component';
import { ToolsSidebarFilterComponent } from '../components/tools-sidebar-filter/tools-sidebar-filter.component';
import { SelectedFilters } from '../models/tool-selected-filter.model';
import { ToolsHelpModalComponent } from '../components/tools-help-modal/tools-help-modal.component';
import { ToolTypeService } from 'src/app/shared/services/tool-type.service';

@Component({
    selector: 'tooling',
    templateUrl: './tooling.view.html',
    styleUrls: ['./tooling.view.scss'],

    imports: [
        MatIconModule,
        MatDialogModule,
        MatButtonModule,
        NgbModule,
        ReactiveFormsModule,
        ToolsSidebarFilterComponent,
        ToolsDataViewComponent,
        MatSelectModule,
        MatInputModule,
    ],
})
export class ToolingView implements OnInit {
    selectedTab: number = 0;
    defaultToolsQuantity: number = 9;

    isLoading = signal<boolean>(true);
    isSearchSelected: boolean = false;
    previousUrl: string = '';
    currentPage = signal<number>(1);
    pageSize: number = 9;
    totalCount = signal<number>(0);
    totalPages = signal<number>(0);
    tools = signal<ToolPreview[]>([]);

    helpClicked: boolean = false;
    resetFilters: boolean = false;
    personasFilter?: number[] = undefined;
    lifeCyclePhasesFilter?: number[] = undefined;
    productTeamsFilter?: number[] = undefined;
    toolTypesFilter?: number[] = undefined;
    keywordsFilter?: string = undefined;

    searchTextFilter = new FormGroup({
        textFilter: new FormControl(''),
    });

    get textFilter() {
        return this.searchTextFilter.get('textFilter');
    }

    private readonly toolService = inject(ToolService);
    private readonly personaService = inject(PersonaService);
    private readonly layout = inject(LayoutService);
    private readonly toolTypeService = inject(ToolTypeService);
    private readonly route = inject(ActivatedRoute);
    private readonly dialogHelper = inject(DialogHelper);
    private readonly windowHelper = inject(WindowHelper);
    private readonly location = inject(Location);
    private readonly dialog = inject(MatDialog);

    personas = this.personaService.personas;
    toolTypes = this.toolTypeService.toolTypes;
    productTeams: ToolProductTeam[] = [];

    constructor() {
        this.layout.setClass('layout-12');
    }

    isSidebarCollapsed: boolean = false;

    toggleSidebar(): void {
        this.isSidebarCollapsed = !this.isSidebarCollapsed;
    }

    // Get the enum values as numbers for filtering
    lifeCyclePhases: LifeCyclePhase[] = Object.keys(LifeCyclePhase)
        .filter(key => isNaN(Number(key)))
        .filter(c => c !== 'None')
        .map(key => LifeCyclePhase[key as keyof typeof LifeCyclePhase]);

    onTabChange(event: Event, index: number) {
        this.selectedTab = index;
        // Prevent tab change from updating state
        event.preventDefault();
    }

    ngOnInit(): void {
        this.toolService.getProductTeams().subscribe(productTeams => {
            this.productTeams = productTeams;
        });

        this.toolTypeService.loadToolTypes();

        this.route.queryParams.subscribe(params => {
            const id = this.getIdFromParam(params, 'id');
            if (id) {
                this.dialogHelper.openToolModalDialog(id);
            }
        });

        // Only load tools if there's no active persona
        // (if there is, change detection will trigger the load)
        const activePersona = this.personaService.activePersona();
        if (!activePersona) {
            this.loadTools();
        }

        this.location.onUrlChange(url => {
            if (this.isSearchSelected && this.previousUrl.endsWith('/tooling') && url.endsWith('/tooling')) {
                this.resetPage();
            }
            this.previousUrl = url;
        });
    }

    getIdFromParam(params: any, key: string): number | null {
        const lowerCaseKey = key.toLowerCase();
        for (const paramKey in params) {
            if (params.hasOwnProperty(paramKey) &&
                paramKey.toLowerCase() === lowerCaseKey
            ) {
                return Number(params[paramKey]);
            }
        }
        return null;
    }

    loadTools() {
        this.isLoading.set(true);
        this.tools.set([]);

        const toolParams = {
            pageNumber: this.currentPage(),
            pageSize: this.pageSize,
            keywords: this.keywordsFilter,
            lifeCyclePhases: this.lifeCyclePhasesFilter,
            productTeams: this.productTeamsFilter,
            personas: this.personasFilter,
            toolTypes: this.toolTypesFilter,
        };

        this.toolService.getTools(toolParams).subscribe({
            next: (data) => {
                if (data && data.data.length > 0) {
                    this.updateValues(data);
                } else {
                    this.isLoading.set(false);
                }
            },
            error: (error) => {
                console.error('Error loading tools:', error);
                this.isLoading.set(false);
            },
        });
    }

    onSubmitSearch() {
        if (this.isSearchSelected == false) {
            this.location.go('/tooling');
            this.isSearchSelected = true;
        }

        this.currentPage.set(1);
        this.keywordsFilter = this.textFilter?.value
            ? this.textFilter.value
            : undefined;

        this.loadTools();
    }

    onFilterValuesChange(filters: SelectedFilters): void {
        if (filters.isCleared) this.searchTextFilter.patchValue({ textFilter: '' });

        this.personasFilter =
            filters.personas.length > 0 ? filters.personas : undefined;
        this.lifeCyclePhasesFilter =
            filters.lifeCyclePhases.length > 0
                ? filters.lifeCyclePhases
                : undefined;
        this.productTeamsFilter =
            filters.productTeams.length > 0 ? filters.productTeams : undefined;
        this.toolTypesFilter = filters.toolTypes.length > 0 ? filters.toolTypes : undefined;

        // Reset to first page when filters change
        this.currentPage.set(1);

        // Trigger search when filters are applied
        this.onSubmitSearch();
    }

    updateValues(tools: PagedResponse<ToolPreview>) {
        // Replace tools instead of concatenating to fix the accumulation bug
        this.tools.set(tools.data);

        this.currentPage.set(tools.pageNumber);
        this.pageSize = tools.pageSize;
        this.totalCount.set(tools.totalCount);
        this.totalPages.set(tools.totalPages);

        this.isLoading.set(false);
    }

    onPageChange(newPage: number): void {
        this.currentPage.set(newPage);
        this.loadTools();
    }

    resetPage() {
        this.windowHelper.reloadPage();
    }

    clearSearchInput(): void {
        this.searchTextFilter.patchValue({ textFilter: '' });
        // Optionally trigger search to show all results
        if (this.isSearchSelected) {
            this.onSubmitSearch();
        }
    }

    onHelpClick(): void {
        this.helpClicked = true;
        this.openHelpDialog();
    }

    openHelpDialog(): void {
        this.dialog.open(ToolsHelpModalComponent);
    }
}
